import axios from "axios";
import { useEffect, useRef, useState } from "react";
import PageHeader from "../shared-components/PageHeader";
import { useCompaniesFetchAll } from "../api/CompaniesApi";
import {
  useActivePrompt,
  useExtractText,
  useGeneratePrompt,
  usePromptAuditLog,
  useSavePrompt,
  useTestPrompt,
} from "../api/CustomBehaviorPromptsApi";
import InputConfiguration from "./InputConfiguration";
import OperationErrorDialog, {
  OperationErrorDialogContent,
} from "./OperationErrorDialog";
import PayloadPlayground from "./PayloadPlayground";
import { EXCLUDED_LABEL, INCLUDED_LABEL } from "./filterTestHelpers";
import PromptAuditLog from "./PromptAuditLog";
import {
  FilterTestRow,
  InputMethod,
  PromptAuditEntry,
  PromptDesign,
} from "./types";

const PROMPT_SERVER_ERROR_TITLE = "Service temporarily unavailable";

const PROMPT_SERVER_ERROR_MESSAGE =
  "We were unable to complete this request due to a server error. Please try again shortly. If the issue persists, contact Fama support.";

interface PromptApiErrorOptions {
  title: string;
  fallbackMessage: string;
}

function promptApiErrorDialogContent(
  error: unknown,
  options: PromptApiErrorOptions
): OperationErrorDialogContent {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status && status >= 500) {
      return {
        title: PROMPT_SERVER_ERROR_TITLE,
        message: PROMPT_SERVER_ERROR_MESSAGE,
      };
    }

    const detail = error.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return { title: options.title, message: detail };
    }
  }

  if (error instanceof Error && error.message) {
    return { title: options.title, message: error.message };
  }

  return { title: options.title, message: options.fallbackMessage };
}

function splitLines(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function inputSourceFromFilename(filename: string | undefined): string {
  const ext = filename?.split(".").pop()?.toLowerCase();
  if (ext === "pdf" || ext === "docx" || ext === "txt") return ext;
  return "document";
}

function metadataString(
  entry: PromptAuditEntry,
  key: string
): string | undefined {
  const value = entry.audit_metadata?.[key];
  if (typeof value === "string" && value.trim()) return value;
  return undefined;
}

function metadataStringList(
  entry: PromptAuditEntry,
  key: string
): string[] | undefined {
  const value = entry.audit_metadata?.[key];
  if (!Array.isArray(value)) return undefined;
  return value.filter((item): item is string => typeof item === "string");
}

function promptDesignFromAuditEntry(
  entry: PromptAuditEntry
): PromptDesign | undefined {
  const promptPayload = metadataString(entry, "prompt_payload");
  if (!promptPayload) return undefined;

  return {
    prompt_payload: promptPayload,
    source_summary: metadataString(entry, "source_summary"),
    included_rules: metadataStringList(entry, "included_rules"),
    excluded_material: metadataStringList(entry, "excluded_material"),
    ambiguities: metadataStringList(entry, "ambiguities"),
    warnings: metadataStringList(entry, "warnings"),
    quality_decision:
      metadataString(entry, "quality_decision") === "needs_revision"
        ? "needs_revision"
        : "ready",
    confidence:
      typeof entry.audit_metadata?.confidence === "number"
        ? entry.audit_metadata.confidence
        : undefined,
    design_session_id: metadataString(entry, "design_session_id"),
  };
}

function activeDesignSessionIdFrom(
  entries: PromptAuditEntry[] | undefined,
  activePromptExternalId: string | undefined
): string | undefined {
  const savedEntry = entries?.find((entry) => {
    if (entry.action !== "saved") return false;
    if (
      activePromptExternalId &&
      entry.custom_behavior_external_id !== activePromptExternalId
    ) {
      return false;
    }
    return Boolean(metadataString(entry, "design_session_id"));
  });

  if (!savedEntry) return undefined;
  return metadataString(savedEntry, "design_session_id");
}

const CustomBehaviorPrompts = () => {
  const {
    mutate: getAllCompanies,
    data: companiesInfo,
    isLoading: companiesFetchLoading,
    isIdle: companiesFetchIdle,
  } = useCompaniesFetchAll();
  const companiesLoading = companiesFetchLoading || companiesFetchIdle;

  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");
  const selectedCompanyUuidRef = useRef(selectedCompanyUuid);
  const [inputMethod, setInputMethod] = useState<InputMethod>("text");
  const [sourceText, setSourceText] = useState("");
  const [customerIntent, setCustomerIntent] = useState("");
  const [sourceFilename, setSourceFilename] = useState<string | undefined>(
    undefined
  );

  const [design, setDesign] = useState<PromptDesign | undefined>(undefined);
  const [designSessionId, setDesignSessionId] = useState<string | undefined>(
    undefined
  );
  const [promptPayload, setPromptPayload] = useState("");

  const [samplePostsText, setSamplePostsText] = useState("");
  const [testRows, setTestRows] = useState<FilterTestRow[] | undefined>(
    undefined
  );

  const [errorDialog, setErrorDialog] =
    useState<OperationErrorDialogContent | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const showApiError = (error: unknown, options: PromptApiErrorOptions) => {
    setErrorDialog(promptApiErrorDialogContent(error, options));
  };

  const generateMutation = useGeneratePrompt();
  const extractMutation = useExtractText();
  const testMutation = useTestPrompt();
  const saveMutation = useSavePrompt();
  const auditLog = usePromptAuditLog(selectedCompanyUuid || undefined);
  const activePrompt = useActivePrompt(selectedCompanyUuid || undefined);

  useEffect(() => {
    getAllCompanies();
  }, [getAllCompanies]);

  useEffect(() => {
    selectedCompanyUuidRef.current = selectedCompanyUuid;
  }, [selectedCompanyUuid]);

  const isSelectedCompany = (companyUuid: string) =>
    selectedCompanyUuidRef.current === companyUuid;
  const activePromptData = activePrompt.isError ? null : activePrompt.data;
  const activeDesignSessionId = activePromptData
    ? activeDesignSessionIdFrom(auditLog.data, activePromptData.external_id)
    : undefined;

  // When a company is selected, load its saved active prompt into the playground
  // so switching companies picks up prior work instead of starting blank. A fresh
  // generation takes precedence over the saved value.
  useEffect(() => {
    if (!selectedCompanyUuid || design || !activePromptData) return;
    setPromptPayload(activePromptData.prompt_payload ?? "");
  }, [selectedCompanyUuid, design, activePromptData]);

  useEffect(() => {
    if (!selectedCompanyUuid || design || !activePromptData) return;
    if (!activeDesignSessionId) return;
    setDesignSessionId((current) => current ?? activeDesignSessionId);
  }, [selectedCompanyUuid, design, activePromptData, activeDesignSessionId]);

  const handleSelectCompany = (companyUuid: string) => {
    // DropdownCombo can fire onChange for the same company (re-select or
    // initial remount sync). Skip the reset so unsaved prompt work is kept.
    if (companyUuid === selectedCompanyUuidRef.current) return;
    selectedCompanyUuidRef.current = companyUuid;
    setSelectedCompanyUuid(companyUuid);
    setDesign(undefined);
    setDesignSessionId(undefined);
    setPromptPayload("");
    setSamplePostsText("");
    setTestRows(undefined);
    setSaveSuccess(false);
    setErrorDialog(null);
  };

  const handleFileSelected = (filename: string, text: string) => {
    setSourceFilename(filename);
    setSourceText(text);
  };

  const handleGenerate = () => {
    const requestCompanyUuid = selectedCompanyUuid;
    if (!requestCompanyUuid) return;
    setErrorDialog(null);
    setSaveSuccess(false);
    const samplePosts = splitLines(samplePostsText);
    generateMutation.mutate(
      {
        companyUuid: requestCompanyUuid,
        sourceText,
        customerIntent,
        samplePosts,
        inputSource:
          inputMethod === "document"
            ? inputSourceFromFilename(sourceFilename)
            : "text",
        sourceFilename: inputMethod === "document" ? sourceFilename : undefined,
      },
      {
        onSuccess: (result) => {
          if (!isSelectedCompany(requestCompanyUuid)) return;
          setDesign(result);
          setDesignSessionId(result.design_session_id);
          setPromptPayload(result.prompt_payload ?? "");
          setTestRows(undefined);
          const suggested = (result.suggested_test_cases ?? [])
            .map((testCase) => testCase.text)
            .filter(Boolean);
          if (suggested.length > 0 && samplePosts.length === 0) {
            setSamplePostsText(suggested.join("\n"));
          }
          auditLog.refetch();
        },
        onError: (error) => {
          if (!isSelectedCompany(requestCompanyUuid)) return;
          showApiError(error, {
            title: "Unable to generate prompt",
            fallbackMessage:
              "Prompt generation could not be completed. Please try again.",
          });
        },
      }
    );
  };

  const handleTest = () => {
    const requestCompanyUuid = selectedCompanyUuid;
    if (!requestCompanyUuid) {
      setErrorDialog({
        title: "Select a company",
        message: "Select a company before testing this prompt.",
      });
      return;
    }
    if (activePrompt.isError) {
      setErrorDialog({
        title: "Unable to load active prompt",
        message:
          "Retry loading this company's active prompt before testing changes.",
      });
      return;
    }

    testMutation.mutate(
      {
        companyUuid: requestCompanyUuid,
        promptPayload,
        posts: splitLines(samplePostsText),
      },
      {
        onSuccess: (rows) => {
          if (!isSelectedCompany(requestCompanyUuid)) return;
          setTestRows(rows);
          auditLog.refetch();
        },
        onError: (error) => {
          if (!isSelectedCompany(requestCompanyUuid)) return;
          showApiError(error, {
            title: "Unable to test prompt",
            fallbackMessage:
              "The prompt test could not be completed. Please try again.",
          });
        },
      }
    );
  };

  const handleSave = () => {
    const requestCompanyUuid = selectedCompanyUuid;
    if (!requestCompanyUuid) return;
    if (activePrompt.isError) {
      setErrorDialog({
        title: "Unable to load active prompt",
        message:
          "Retry loading this company's active prompt before saving changes.",
      });
      return;
    }
    setErrorDialog(null);
    setSaveSuccess(false);
    saveMutation.mutate(
      {
        companyUuid: requestCompanyUuid,
        promptPayload,
        positiveName: INCLUDED_LABEL,
        negativeName: EXCLUDED_LABEL,
        autoRemove: true,
        designSessionId: designSessionId ?? activeDesignSessionId,
      },
      {
        onSuccess: () => {
          if (!isSelectedCompany(requestCompanyUuid)) return;
          setSaveSuccess(true);
          auditLog.refetch();
          activePrompt.refetch();
        },
        onError: (error) => {
          if (!isSelectedCompany(requestCompanyUuid)) return;
          showApiError(error, {
            title: "Unable to save prompt",
            fallbackMessage:
              "The active prompt could not be saved. Please try again.",
          });
        },
      }
    );
  };

  const handleRestoreVersion = (entry: PromptAuditEntry) => {
    const restoredDesign = promptDesignFromAuditEntry(entry);
    if (!restoredDesign) {
      setErrorDialog({
        title: "Unable to restore version",
        message:
          "This log entry does not include a restorable prompt payload. Choose a generated version with a stored payload.",
      });
      return;
    }

    setDesign(restoredDesign);
    setDesignSessionId(restoredDesign.design_session_id);
    setPromptPayload(restoredDesign.prompt_payload);
    setTestRows(undefined);
    setSaveSuccess(false);
    setErrorDialog(null);
  };

  const canSave =
    Boolean(selectedCompanyUuid) &&
    promptPayload.trim().length > 0 &&
    !activePrompt.isError;
  const canTest =
    canSave &&
    splitLines(samplePostsText).length > 0 &&
    !testMutation.isLoading;
  const activePromptLoadError = activePrompt.isError
    ? promptApiErrorDialogContent(activePrompt.error, {
        title: "Unable to load active prompt",
        fallbackMessage:
          "We could not load this company's saved active prompt. Retry before assuming the company has no active prompt.",
      })
    : null;

  return (
    <>
      <PageHeader title="Custom Behavior Prompt Design" />
      <p className="mb-6 max-w-3xl text-sm text-gray-500">
        Internal support tool. Generate a company-specific misconduct filtering
        prompt from a handbook or pasted guidelines, test it against sample
        posts, and save the active definition.
      </p>

      <div className="flex flex-col gap-6">
        <InputConfiguration
          companies={companiesInfo ?? []}
          companiesLoading={companiesLoading}
          selectedCompanyUuid={selectedCompanyUuid}
          onSelectCompany={handleSelectCompany}
          inputMethod={inputMethod}
          onInputMethodChange={setInputMethod}
          sourceText={sourceText}
          onSourceTextChange={setSourceText}
          customerIntent={customerIntent}
          onCustomerIntentChange={setCustomerIntent}
          sourceFilename={sourceFilename}
          onFileSelected={handleFileSelected}
          onExtractFile={(file) =>
            extractMutation.mutateAsync({
              companyUuid: selectedCompanyUuid,
              file,
            })
          }
          onExtractApiError={(error) =>
            showApiError(error, {
              title: "Unable to extract document text",
              fallbackMessage:
                "We could not extract text from this file. Please try a different format or paste the text manually.",
            })
          }
          isExtracting={extractMutation.isLoading}
          onGenerate={handleGenerate}
          isGenerating={generateMutation.isLoading}
        />

        {activePromptLoadError && (
          <div
            className="rounded-md border border-red-200 bg-red-50 p-4"
            data-testid="active-prompt-error"
          >
            <p className="text-sm font-semibold text-red-700">
              {activePromptLoadError.title}
            </p>
            <p className="mt-1 text-sm text-red-600">
              {activePromptLoadError.message}
            </p>
            <button
              type="button"
              className="mt-2 text-sm font-medium text-red-700 hover:text-red-800"
              onClick={() => activePrompt.refetch()}
            >
              Retry loading active prompt
            </button>
          </div>
        )}

        <PayloadPlayground
          design={design}
          promptPayload={promptPayload}
          onPromptPayloadChange={setPromptPayload}
          samplePostsText={samplePostsText}
          onSamplePostsTextChange={setSamplePostsText}
          onTest={handleTest}
          isTesting={testMutation.isLoading}
          testRows={testRows}
          onSave={handleSave}
          isSaving={saveMutation.isLoading}
          saveSuccess={saveSuccess}
          canTest={canTest}
          canSave={canSave}
        />

        <OperationErrorDialog
          content={errorDialog}
          onClose={() => setErrorDialog(null)}
        />

        <PromptAuditLog
          entries={auditLog.data}
          isLoading={auditLog.isLoading}
          isError={auditLog.isError}
          companySelected={Boolean(selectedCompanyUuid)}
          activeDesignSessionId={activeDesignSessionId}
          onRestoreVersion={handleRestoreVersion}
        />
      </div>
    </>
  );
};

export default CustomBehaviorPrompts;
